/**
 * rb-tools.js — the tool panes of the editor column: Design, Check, Job
 * (tailor to a posting), Letter, hh.ru.
 *
 * Each pane merges what used to be a separate modal (templates, ATS score,
 * job-description match, cover letter, region panel) into one place reached
 * from the rail — the old tool spread one job over four dialogs and a panel at
 * the top of the form (audit 2026-10-09).
 *
 * The free, instant layer always works (rules, keyword match, the template
 * letter); the assistant is the deeper layer on top, behind its own button.
 *
 * Depends on: RbState, RbI18n, RbChecks, RbJd, RbAi, RbConventions, RbTemplates,
 * RbPreview, RbCoverLetter. globalThis.RbTools
 */
(function () {
    'use strict';

    var T = function (k, v) { return globalThis.RbI18n.t(k, v); };
    var E = function (s) { return globalThis.RbAi.esc(s); };
    var S = globalThis.RbState;

    var _pane = null, _cx = null, _cur = null;
    var _review = null, _tailor = null, _tplFilter = 'rec';

    function m() { return _cx.model(); }
    function lang() { return m().meta.lang || globalThis.RbI18n.LOCALE; }
    function head(t, sub, extra) {
        return '<div class="rbx-pane-h"><h3 class="rbx-pane-t">' + E(T(t)) + '</h3>' + (extra || '') + '</div>' +
            (sub ? '<p class="rbx-pane-sub">' + E(T(sub)) + '</p>' : '');
    }
    function ring(score, size) {
        var r = 26, c = 2 * Math.PI * r;
        var col = score >= 80 ? 'var(--lg-success)' : score >= 55 ? 'var(--lg-warn)' : 'var(--lg-danger)';
        return '<svg viewBox="0 0 64 64" aria-hidden="true"' + (size ? ' style="width:' + size + 'px;height:' + size + 'px"' : '') + '><circle cx="32" cy="32" r="' + r + '" fill="none" stroke="currentColor" stroke-opacity=".14" stroke-width="7"/>' +
            '<circle cx="32" cy="32" r="' + r + '" fill="none" stroke="' + col + '" stroke-width="7" stroke-linecap="round" stroke-dasharray="' + c.toFixed(1) + '" stroke-dashoffset="' + (c * (1 - score / 100)).toFixed(1) + '" transform="rotate(-90 32 32)"/>' +
            '<text x="32" y="38" text-anchor="middle" font-size="17" font-weight="800" fill="currentColor">' + score + '</text></svg>';
    }

    /* ref like "exp2.description" / "summary" / "skills" → where to go */
    function refTarget(ref) {
        var r = String(ref || '');
        var mm = r.match(/^(exp|edu|proj|vol|cert|award|pub)(\d+)(?:\.(\w+))?$/);
        if (mm) {
            var sec = { exp: 'experience', edu: 'education', proj: 'projects', vol: 'volunteer', cert: 'certifications', award: 'awards', pub: 'publications' }[mm[1]];
            return { pane: sec, idx: +mm[2] - 1, field: mm[3] || null };
        }
        if (r === 'summary') return { pane: 'summary', field: 'summary' };
        if (r === 'title') return { pane: 'personal', field: 'title' };
        if (r === 'skills') return { pane: 'skills' };
        if (r === 'languages') return { pane: 'languages' };
        if (/^custom\d+$/.test(r)) return { pane: 'custom:' + (+r.slice(6) - 1) };
        return null;
    }
    /* the model value a ref names, and a setter for it */
    function refField(ref) {
        var t = refTarget(ref), md = m();
        if (!t) return null;
        if (t.pane === 'summary') return { get: function () { return md.summary; }, set: function (v) { md.summary = v; } };
        if (t.pane === 'personal') return { get: function () { return md.personal.title; }, set: function (v) { md.personal.title = v; } };
        if (t.pane === 'skills') return {
            get: function () { return md.skills.map(function (k) { return k.name; }).join(', '); },
            set: function (v) {
                var names = String(v).split(/[,;\n]/).map(function (x) { return x.trim(); }).filter(Boolean);
                md.skills = names.map(function (n) {
                    var old = md.skills.filter(function (k) { return k.name.toLowerCase() === n.toLowerCase(); })[0];
                    return old || Object.assign(S.newEntry('skills'), { name: n });
                });
            }
        };
        var e = (md[t.pane] || [])[t.idx];
        if (!e || !t.field) return null;
        return { get: function () { return e[t.field]; }, set: function (v) { e[t.field] = v; } };
    }

    function goBtn(target, label) {
        if (!target) return '';
        return '<button type="button" class="rbx-go" data-go=\'' + E(JSON.stringify(target)) + '\'>' + E(label || T('chk_fix')) + ' ›</button>';
    }
    function bindGo(root) {
        root.querySelectorAll('[data-go]').forEach(function (b) {
            b.addEventListener('click', function () {
                var t = JSON.parse(b.getAttribute('data-go'));
                _cx.focus(t.pane, t);
            });
        });
    }

    /* ═══ Design ════════════════════════════════════════════════════════ */
    function designPane() {
        var md = m();
        var regions = globalThis.RbConventions.regions();
        var prof = globalThis.RbConventions.get(md.meta.region);
        var rec = globalThis.RbConventions.recommendedTemplates(md.meta.region);
        var cats = ['rec', 'all'].concat(globalThis.RbTemplates.CATEGORIES);
        var list = globalThis.RbTemplates.LIST.filter(function (t) {
            return _tplFilter === 'all' || (_tplFilter === 'rec' ? rec.indexOf(t.id) > -1 : t.category === _tplFilter);
        });
        if (!list.length) list = globalThis.RbTemplates.LIST;
        var h = head('nav_design', 'pane_design_sub');
        h += '<div class="tc-chips" role="group" aria-label="' + E(T('templates_title')) + '" style="margin-bottom:10px">' + cats.map(function (c) {
            var label = c === 'rec' ? T('cat_recommended') : c === 'all' ? T('cat_all') : T('cat_' + c);
            return '<button type="button" class="tc-chip' + (c === _tplFilter ? ' on' : '') + '" data-cat="' + c + '" aria-pressed="' + (c === _tplFilter) + '">' + E(label) + '</button>';
        }).join('') + '</div>';
        h += '<div class="rbx-tpls" id="rbTplGrid">' + list.map(function (t) {
            return '<button type="button" class="rbx-tpl" data-tpl="' + t.id + '" aria-pressed="' + (md.meta.template === t.id) + '">' +
                '<span class="rbx-tpl-th" data-th="' + t.id + '"></span>' +
                '<span class="rbx-tpl-n">' + E(T(t.nameKey)) + (rec.indexOf(t.id) > -1 ? '<small>' + E(T('recommended_short')) + '</small>' : '') + '</span></button>';
        }).join('') + '</div>';

        h += '<h4 class="rbx-sub">' + E(T('colors_title')) + '</h4><div class="rbx-swatches" role="group" aria-label="' + E(T('colors_title')) + '">' +
            globalThis.RbI18n.SCHEME_ORDER.map(function (id) {
                var c = globalThis.RbI18n.SCHEMES[id];
                return '<button type="button" class="rbx-sw" data-scheme="' + id + '" aria-pressed="' + (md.meta.scheme === id) + '" aria-label="' + E(T('scheme_' + id)) + '" title="' + E(T('scheme_' + id)) + '" style="background:' + c.accent + '"></button>';
            }).join('') + '</div>';

        var langs = globalThis.RbI18n.docLangs();
        var dn = null;
        try { dn = new Intl.DisplayNames([globalThis.RbI18n.LOCALE], { type: 'language' }); } catch (e) {}
        var cap = function (s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; };
        h += '<h4 class="rbx-sub">' + E(T('format_title')) + '</h4><div class="rbx-2">' +
            '<div class="rbx-f"><label class="rbx-f-l" for="rbLangSel">' + E(T('resume_lang')) + '</label><select class="rbx-sel" id="rbLangSel">' +
            langs.map(function (l) { return '<option value="' + l + '"' + (lang() === l ? ' selected' : '') + '>' + E(cap(dn ? dn.of(l) : l)) + '</option>'; }).join('') + '</select>' +
            '<p class="rbx-hint">' + E(T('resume_lang_hint')) + '</p></div>' +
            '<div class="rbx-f"><label class="rbx-f-l" for="rbRegionSel">' + E(T('region_label')) + '</label><select class="rbx-sel" id="rbRegionSel">' +
            regions.map(function (r) { return '<option value="' + r.id + '"' + (md.meta.region === r.id ? ' selected' : '') + '>' + E(T('format_' + r.id)) + '</option>'; }).join('') + '</select></div>' +
            '</div><div class="rbx-2"><div class="rbx-f"><label class="rbx-f-l" for="rbPageSel">' + E(T('page_size')) + '</label><select class="rbx-sel" id="rbPageSel">' +
            ['a4', 'letter'].map(function (ps) { return '<option value="' + ps + '"' + (md.meta.pageSize === ps ? ' selected' : '') + '>' + E(T('page_' + ps)) + '</option>'; }).join('') + '</select></div></div>';

        var conv = T(prof.convention_key);
        h += '<details class="rbx-det"><summary>' + E(T('region_norms_title', { country: T('format_' + md.meta.region) })) + '</summary><div class="rbx-det-body">' +
            '<p class="rbx-hint" style="margin-bottom:8px">' + E(conv) + '</p><p class="rbx-hint">' + E(T(prof.sources_key)) + '</p></div></details>';

        var fields = [['photo', 'show_photo'], ['dob', 'show_dob'], ['marital', 'show_marital'], ['nationality', 'show_nationality'],
            ['religion', 'show_religion'], ['gender', 'show_gender'], ['signature', 'show_signature']];
        h += '<h4 class="rbx-sub">' + E(T('personal_fields_title')) + '</h4>' + fields.map(function (f) {
            var st = globalThis.RbConventions.fieldState(md.meta.region, f[0]);
            var tag = st === 'on' ? T('region_expected') : st === 'off' ? T('region_not_expected') : '';
            return '<label class="rbx-check"><input type="checkbox" data-cult="' + f[0] + '"' + (md.cultural[f[0]] ? ' checked' : '') + '> ' + E(T(f[1])) +
                (tag ? ' <span class="rbx-chip ' + (st === 'off' ? 'miss' : 'ok') + '" style="padding:1px 8px">' + E(tag) + '</span>' : '') + '</label>';
        }).join('');

        var order = (md.meta.sectionOrder || []).filter(function (s) { return s !== 'personal'; });
        var visible = order.filter(function (s) { return s === 'summary' || (md[s] || []).length; });
        h += '<h4 class="rbx-sub">' + E(T('order_title')) + '</h4><ol class="rbx-order">' + visible.map(function (s, i) {
            return '<li><span>' + E(T('section_' + s)) + '</span>' +
                '<button type="button" class="rbx-ib" data-ord="up" data-sec="' + s + '" aria-label="' + E(T('move_up') + ': ' + T('section_' + s)) + '"' + (i === 0 ? ' disabled' : '') + '>↑</button>' +
                '<button type="button" class="rbx-ib" data-ord="down" data-sec="' + s + '" aria-label="' + E(T('move_down') + ': ' + T('section_' + s)) + '"' + (i === visible.length - 1 ? ' disabled' : '') + '>↓</button></li>';
        }).join('') + '</ol>';
        return h;
    }

    function fillThumbs() {
        var md = m();
        _pane.querySelectorAll('[data-th]').forEach(function (th) {
            var w = th.clientWidth || 100;
            th.innerHTML = '<div style="transform:scale(' + (w / 794).toFixed(4) + ')">' + globalThis.RbPreview.thumbHtml(md, th.dataset.th, _cx.renderOpts()) + '</div>';
        });
    }

    function bindDesign() {
        var md = m();
        _pane.querySelectorAll('[data-cat]').forEach(function (b) {
            b.addEventListener('click', function () { _tplFilter = b.dataset.cat; show('design', true); });
        });
        _pane.querySelectorAll('[data-tpl]').forEach(function (b) {
            b.addEventListener('click', function () {
                md.meta.template = b.dataset.tpl;
                _pane.querySelectorAll('[data-tpl]').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
                _cx.track('template', { template: b.dataset.tpl });
                _cx.changed('design');
            });
        });
        _pane.querySelectorAll('[data-scheme]').forEach(function (b) {
            b.addEventListener('click', function () {
                md.meta.scheme = b.dataset.scheme;
                _pane.querySelectorAll('[data-scheme]').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
                _cx.changed('design');
                fillThumbs();
            });
        });
        var ls = _pane.querySelector('#rbLangSel');
        if (ls) ls.addEventListener('change', function () { md.meta.lang = ls.value; _cx.track('resume_lang', { lang: ls.value }); _cx.changed('design'); fillThumbs(); });
        var rs = _pane.querySelector('#rbRegionSel');
        if (rs) rs.addEventListener('change', function () { _cx.applyRegion(rs.value); show('design', true); });
        var ps = _pane.querySelector('#rbPageSel');
        if (ps) ps.addEventListener('change', function () { md.meta.pageSize = ps.value; _cx.changed('design'); });
        _pane.querySelectorAll('[data-cult]').forEach(function (cb) {
            cb.addEventListener('change', function () { md.cultural[cb.dataset.cult] = cb.checked; _cx.changed('design'); });
        });
        _pane.querySelectorAll('[data-ord]').forEach(function (b) {
            b.addEventListener('click', function () {
                var arr = md.meta.sectionOrder, i = arr.indexOf(b.dataset.sec);
                var visible = arr.filter(function (s) { return s !== 'personal' && (s === 'summary' || (md[s] || []).length); });
                var vi = visible.indexOf(b.dataset.sec);
                var other = visible[b.dataset.ord === 'up' ? vi - 1 : vi + 1];
                if (!other) return;
                var j = arr.indexOf(other);
                arr[i] = other; arr[j] = b.dataset.sec;
                _cx.structure();
                show('design', true);
                var again = _pane.querySelector('[data-ord="' + b.dataset.ord + '"][data-sec="' + b.dataset.sec + '"]');
                if (again && !again.disabled) again.focus();
            });
        });
        requestAnimationFrame(fillThumbs);
    }

    /* ═══ Check ═════════════════════════════════════════════════════════ */
    function checkPane() {
        var r = _cx.checks();
        var failed = r.issues.filter(function (i) { return !i.pass; });
        var passed = r.issues.filter(function (i) { return i.pass; });
        var h = head('nav_check', 'pane_check_sub');
        h += '<div class="rbx-score">' + ring(r.score) + '<div><p class="rbx-score-t">' + E(T('chk_band_' + r.band)) + '</p>' +
            '<p class="rbx-score-s">' + E(failed.length ? T('chk_left', { n: failed.length }) : T('chk_all_ok')) + '</p></div></div>';
        if (failed.length) {
            h += '<ul class="rbx-issues">' + failed.map(function (i) {
                return '<li class="rbx-issue" data-sev="' + i.sev + '"><span class="rbx-issue-ico" aria-hidden="true">' + (i.sev === 'high' ? '⛔' : i.sev === 'medium' ? '⚠️' : '💡') + '</span>' +
                    '<div class="rbx-issue-b">' + E(T(i.key, i.vars || {})) + '<br>' + goBtn(i.pane ? { pane: i.pane, idx: i.idx, field: i.field } : null) + '</div></li>';
            }).join('') + '</ul>';
        }
        if (passed.length) {
            h += '<details class="rbx-det"><summary>' + E(T('chk_passed', { n: passed.length })) + '</summary><div class="rbx-det-body"><ul class="rbx-issues">' +
                passed.map(function (i) { return '<li class="rbx-issue" data-sev="pass"><span class="rbx-issue-ico" aria-hidden="true">✓</span><div class="rbx-issue-b">' + E(T(i.key + '_ok', i.vars || {})) + '</div></li>'; }).join('') +
                '</ul></div></details>';
        }
        if (globalThis.RbAi.live()) {
            h += '<h4 class="rbx-sub">' + E(T('review_title')) + '</h4><p class="rbx-hint" style="margin-bottom:8px">' + E(T('review_sub')) + '</p>' +
                '<button type="button" class="rbx-ai-btn" data-review>✨ ' + E(T(_review ? 'review_again' : 'review_go')) + '</button><div data-ai-host></div>' +
                '<div data-review-out>' + (_review ? reviewHtml(_review) : '') + '</div>';
        }
        return h;
    }
    function reviewHtml(rv) {
        var h = '<div class="rbx-sugg"><div class="rbx-score" style="margin:0 0 8px">' + ring(rv.score, 52) + '<div><p class="rbx-score-t">' + E(T('review_score')) + '</p><p class="rbx-score-s">' + E(rv.verdict) + '</p></div></div>';
        if (rv.strengths && rv.strengths.length) h += '<p class="rbx-sugg-q" style="margin-top:0">' + E(T('review_strengths')) + '</p><ul class="rbx-sugg-list">' + rv.strengths.map(function (s) { return '<li>' + E(s) + '</li>'; }).join('') + '</ul>';
        if (rv.issues && rv.issues.length) {
            h += '<p class="rbx-sugg-q">' + E(T('review_issues')) + '</p><ul class="rbx-issues" style="margin-top:6px">' + rv.issues.map(function (i) {
                return '<li class="rbx-issue" data-sev="' + E(i.severity) + '"><span class="rbx-issue-ico" aria-hidden="true">' + (i.severity === 'high' ? '⛔' : i.severity === 'medium' ? '⚠️' : '💡') + '</span><div class="rbx-issue-b">' +
                    E(i.problem) + (i.quote ? '<q>«' + E(i.quote) + '»</q>' : '') + (i.fix ? '<em>' + globalThis.RbAi.marked(i.fix) + '</em>' : '') +
                    goBtn(refTarget(i.ref)) + '</div></li>';
            }).join('') + '</ul>';
        }
        return h + '<p class="rbx-disclose">' + globalThis.RbAi.disclosure() + '</p></div>';
    }
    function bindCheck() {
        bindGo(_pane);
        var b = _pane.querySelector('[data-review]');
        if (b) b.addEventListener('click', function () {
            if (S.isEmpty(m())) { toast(T('ai_need_resume'), 'warning'); return; }
            runInPane(b, 'review', { lang: lang(), format: m().meta.region, resume: _cx.payloadModel() }, function (res) {
                _review = res;
                var out = _pane.querySelector('[data-review-out]');
                if (out) { out.innerHTML = reviewHtml(res); bindGo(out); }
                b.textContent = '✨ ' + T('review_again');
            });
        });
    }

    /* ═══ Job (tailor to a posting) ═════════════════════════════════════ */
    function jobPane() {
        var md = m(), tg = md.target;
        var h = head('nav_job', 'pane_job_sub');
        h += '<div class="rbx-f"><label class="rbx-f-l" for="rbJobText">' + E(T('jd_title')) + '</label><textarea class="rbx-ta" id="rbJobText" data-tg="job" rows="7" placeholder="' + E(T('jd_placeholder')) + '">' + E(tg.job) + '</textarea>' +
            '<p class="rbx-hint">' + E(T('jd_hint')) + '</p></div>';
        h += '<div class="rbx-2"><div class="rbx-f"><label class="rbx-f-l" for="rbTgCo"><span>' + E(T('cl_company')) + '</span><span class="rbx-opt">' + E(T('optional')) + '</span></label><input class="rbx-in" id="rbTgCo" data-tg="company" value="' + E(tg.company) + '"></div>' +
            '<div class="rbx-f"><label class="rbx-f-l" for="rbTgPos"><span>' + E(T('cl_position')) + '</span><span class="rbx-opt">' + E(T('optional')) + '</span></label><input class="rbx-in" id="rbTgPos" data-tg="position" value="' + E(tg.position) + '"></div></div>';
        h += '<div data-kw-out></div>';
        if (globalThis.RbAi.live()) {
            h += '<h4 class="rbx-sub">' + E(T('tailor_title')) + '</h4><p class="rbx-hint" style="margin-bottom:8px">' + E(T('tailor_sub')) + '</p>' +
                '<button type="button" class="rbx-ai-btn" data-tailor>✨ ' + E(T('tailor_go')) + '</button><div data-ai-host></div><div data-tailor-out></div>';
        }
        return h;
    }
    function keywordsHtml() {
        var tg = m().target;
        if (String(tg.job || '').trim().length < 40) return '';
        var r = globalThis.RbJd.analyze(m(), tg.job, lang());
        if (!r.keywords.length) return '';
        return '<div class="rbx-score" style="margin:4px 0 6px">' + ring(r.matchPct, 52) + '<div><p class="rbx-score-t">' + E(T('jd_match_t')) + '</p><p class="rbx-score-s">' + E(T('jd_match_s')) + '</p></div></div>' +
            (r.found.length ? '<p class="rbx-hint">' + E(T('jd_found')) + '</p><div class="rbx-chips">' + r.found.map(function (k) { return '<span class="rbx-chip ok">' + E(k) + '</span>'; }).join('') + '</div>' : '') +
            (r.missing.length ? '<p class="rbx-hint">' + E(T('jd_missing')) + '</p><div class="rbx-chips">' + r.missing.map(function (k) { return '<span class="rbx-chip miss">' + E(k) + '</span>'; }).join('') + '</div>' : '');
    }
    function tailorHtml(tr) {
        var h = '<div class="rbx-sugg"><div class="rbx-score" style="margin:0 0 8px">' + ring(tr.score, 52) + '<div><p class="rbx-score-t">' + E(T('tailor_score')) + '</p><p class="rbx-score-s">' + E(tr.verdict) + '</p></div></div>';
        if (tr.matched && tr.matched.length) h += '<p class="rbx-hint">' + E(T('tailor_matched')) + '</p><div class="rbx-chips">' + tr.matched.map(function (k) { return '<span class="rbx-chip ok">' + E(k) + '</span>'; }).join('') + '</div>';
        if (tr.missing && tr.missing.length) {
            h += '<p class="rbx-hint">' + E(T('tailor_missing')) + '</p><ul class="rbx-issues" style="margin-top:6px">' + tr.missing.map(function (x) {
                return '<li class="rbx-issue" data-sev="' + (x.importance === 'must' ? 'medium' : 'low') + '"><span class="rbx-issue-ico" aria-hidden="true">' + (x.evidence === 'partial' ? '◐' : '○') + '</span><div class="rbx-issue-b"><b>' + E(x.term) + '</b>' +
                    ' <span class="rbx-chip" style="padding:0 7px;font-size:12px">' + E(T(x.importance === 'must' ? 'tailor_must' : 'tailor_nice')) + '</span>' + (x.note ? '<em>' + E(x.note) + '</em>' : '') + '</div></li>';
            }).join('') + '</ul>';
        }
        if (tr.edits && tr.edits.length) {
            h += '<p class="rbx-sugg-q">' + E(T('tailor_edits')) + '</p><ul class="rbx-issues" style="margin-top:6px">' + tr.edits.map(function (e, i) {
                var done = e._applied;
                return '<li class="rbx-issue" data-edit="' + i + '"><span class="rbx-issue-ico" aria-hidden="true">' + (done ? '✓' : '✎') + '</span><div class="rbx-issue-b">' +
                    '<div class="rbx-diff"><del>' + E(e.original) + '</del><ins>' + globalThis.RbAi.marked(e.rewritten) + '</ins></div>' + (e.reason ? '<em>' + E(e.reason) + '</em>' : '') +
                    '<div class="rbx-sugg-act" style="margin-top:6px">' + (done
                        ? '<button type="button" class="tc-btn ghost sm" data-edit-undo="' + i + '">' + E(T('ai_undo')) + '</button>'
                        : '<button type="button" class="tc-btn sm" data-edit-apply="' + i + '">' + E(T('ai_apply')) + '</button>') + goBtn(refTarget(e.ref), T('tailor_show')) + '</div></div></li>';
            }).join('') + '</ul>';
        }
        return h + '<p class="rbx-disclose">' + globalThis.RbAi.disclosure() + '</p></div>';
    }
    function bindJob() {
        var md = m();
        var kw = _pane.querySelector('[data-kw-out]');
        var paint = function () { if (kw) kw.innerHTML = keywordsHtml(); };
        paint();
        var tmr = null;
        _pane.querySelectorAll('[data-tg]').forEach(function (inp) {
            inp.addEventListener('input', function () {
                md.target[inp.dataset.tg] = inp.value;
                _cx.changed('meta');
                if (inp.dataset.tg === 'job') { clearTimeout(tmr); tmr = setTimeout(paint, 350); }
            });
        });
        var out = _pane.querySelector('[data-tailor-out]');
        var drawT = function () { if (out && _tailor) { out.innerHTML = tailorHtml(_tailor); bindTailor(out); } };
        drawT();
        var b = _pane.querySelector('[data-tailor]');
        if (b) b.addEventListener('click', function () {
            if (String(md.target.job || '').trim().length < 80) { toast(T('tailor_need_job'), 'warning'); _pane.querySelector('#rbJobText').focus(); return; }
            if (S.isEmpty(md)) { toast(T('ai_need_resume'), 'warning'); return; }
            runInPane(b, 'tailor', { lang: lang(), format: md.meta.region, resume: _cx.payloadModel(), job: md.target.job }, function (res) {
                _tailor = res;
                drawT();
            });
        });
    }
    function bindTailor(out) {
        bindGo(out);
        out.querySelectorAll('[data-edit-apply]').forEach(function (btn) {
            btn.addEventListener('click', function () {
                var e = _tailor.edits[+btn.dataset.editApply];
                var f = refField(e.ref);
                if (!f) return;
                var cur = String(f.get() || '');
                var at = cur.indexOf(e.original);
                if (at < 0) { toast(T('tailor_changed'), 'warning'); return; }
                e._before = cur;
                f.set(cur.slice(0, at) + e.rewritten + cur.slice(at + e.original.length));
                e._applied = true;
                _cx.track('ai_apply', { task: 'tailor' });
                _cx.changed('content');
                out.innerHTML = tailorHtml(_tailor); bindTailor(out);
            });
        });
        out.querySelectorAll('[data-edit-undo]').forEach(function (btn) {
            btn.addEventListener('click', function () {
                var e = _tailor.edits[+btn.dataset.editUndo];
                var f = refField(e.ref);
                if (f && e._before != null) f.set(e._before);
                e._applied = false;
                _cx.changed('content');
                out.innerHTML = tailorHtml(_tailor); bindTailor(out);
            });
        });
    }

    /* ═══ Letter ════════════════════════════════════════════════════════ */
    function letterPane() {
        var md = m(), tg = md.target, L = md.letter;
        var h = head('nav_letter', 'pane_letter_sub');
        h += '<div class="rbx-2"><div class="rbx-f"><label class="rbx-f-l" for="rbLtCo">' + E(T('cl_company')) + '</label><input class="rbx-in" id="rbLtCo" data-tg="company" value="' + E(tg.company) + '"></div>' +
            '<div class="rbx-f"><label class="rbx-f-l" for="rbLtPos">' + E(T('cl_position')) + '</label><input class="rbx-in" id="rbLtPos" data-tg="position" value="' + E(tg.position) + '"></div></div>';
        h += '<div class="rbx-f"><label class="rbx-f-l" for="rbLtMgr"><span>' + E(T('cl_manager')) + '</span><span class="rbx-opt">' + E(T('optional')) + '</span></label><input class="rbx-in" id="rbLtMgr" data-tg="manager" value="' + E(tg.manager) + '"></div>';
        h += '<div class="rbx-f"><label class="rbx-f-l" for="rbLtNotes"><span>' + E(T('cl_notes')) + '</span><span class="rbx-opt">' + E(T('optional')) + '</span></label><textarea class="rbx-ta" id="rbLtNotes" data-lt="notes" rows="2" placeholder="' + E(T('cl_notes_ph')) + '">' + E(L.notes) + '</textarea>' +
            '<p class="rbx-hint">' + E(String(tg.job || '').trim() ? T('cl_job_used') : T('cl_job_none')) + '</p></div>';
        h += '<div class="rbx-ai-row">' + (globalThis.RbAi.live() ? '<button type="button" class="rbx-ai-btn" data-letter-ai>✨ ' + E(T('cl_ai_go')) + '</button>' : '') +
            '<button type="button" class="tc-btn ghost sm" data-letter-tpl>' + E(T('cl_template_go')) + '</button></div><div data-ai-host></div>';
        h += '<div data-letter-edit' + (String(L.body || '').trim() ? '' : ' hidden') + '>' +
            '<h4 class="rbx-sub">' + E(T('cl_text_title')) + '</h4>' +
            '<div class="rbx-f"><label class="rbx-f-l" for="rbLtGreet">' + E(T('cl_greeting_label')) + '</label><input class="rbx-in" id="rbLtGreet" data-lt="greeting" value="' + E(L.greeting) + '"></div>' +
            '<div class="rbx-f"><label class="rbx-f-l" for="rbLtBody">' + E(T('cl_body_label')) + '</label><textarea class="rbx-ta" id="rbLtBody" data-lt="body" rows="12">' + E(L.body) + '</textarea></div>' +
            '<div class="rbx-f"><label class="rbx-f-l" for="rbLtClose">' + E(T('cl_closing_label')) + '</label><input class="rbx-in" id="rbLtClose" data-lt="closing" value="' + E(L.closing) + '"></div>' +
            '<div class="rbx-sugg-act"><button type="button" class="tc-btn sm" data-letter-pdf>📄 PDF</button><button type="button" class="tc-btn ghost sm" data-letter-docx>📝 Word</button>' +
            '<button type="button" class="tc-btn ghost sm" data-letter-copy>📋 ' + E(T('copy_text')) + '</button></div>' +
            '<p class="rbx-hint" style="margin-top:8px">' + E(T('cl_hh_hint')) + '</p></div>';
        return h;
    }
    function bindLetter() {
        var md = m();
        var editWrap = _pane.querySelector('[data-letter-edit]');
        var fill = function (L) {
            md.letter.greeting = L.greeting; md.letter.body = L.body; md.letter.closing = L.closing; md.letter.subject = L.subject || '';
            ['greeting', 'body', 'closing'].forEach(function (k) { var el = _pane.querySelector('[data-lt="' + k + '"]'); if (el) el.value = md.letter[k]; });
            editWrap.hidden = false;
            _cx.changed('letter');
        };
        _pane.querySelectorAll('[data-tg]').forEach(function (inp) {
            inp.addEventListener('input', function () { md.target[inp.dataset.tg] = inp.value; _cx.changed('letter'); });
        });
        _pane.querySelectorAll('[data-lt]').forEach(function (inp) {
            inp.addEventListener('input', function () { md.letter[inp.dataset.lt] = inp.value; _cx.changed('letter'); });
        });
        var tplBtn = _pane.querySelector('[data-letter-tpl]');
        tplBtn.addEventListener('click', function () {
            var go = function () { fill(globalThis.RbCoverLetter.draft(md)); _cx.track('letter_template'); };
            if (String(md.letter.body || '').trim()) _cx.confirm(T('cl_replace_q'), T('cl_replace_d'), T('cl_replace_yes'), go); else go();
        });
        var ai = _pane.querySelector('[data-letter-ai]');
        if (ai) ai.addEventListener('click', function () {
            if (S.isEmpty(md)) { toast(T('ai_need_resume'), 'warning'); return; }
            var run = function () {
                runInPane(ai, 'cover', {
                    lang: lang(), format: md.meta.region, resume: _cx.payloadModel(), job: md.target.job || '',
                    company: md.target.company || '', position: md.target.position || md.personal.title || '',
                    manager: md.target.manager || '', notes: md.letter.notes || ''
                }, function (res) { fill(globalThis.RbCoverLetter.fromAi(res)); });
            };
            if (String(md.letter.body || '').trim()) _cx.confirm(T('cl_replace_q'), T('cl_replace_d'), T('cl_replace_yes'), run); else run();
        });
        _pane.querySelector('[data-letter-pdf]').addEventListener('click', function () {
            if (_cx.guardExport('letter')) return;
            toast(T('export_opening_print'));
            globalThis.RbCoverLetter.exportPdf(md, { rtl: _cx.rtl() }).then(function () { _cx.track('export', { format: 'letter_pdf' }); }, _cx.exportError('letter_pdf'));
        });
        _pane.querySelector('[data-letter-docx]').addEventListener('click', function () {
            if (_cx.guardExport('letter')) return;
            globalThis.RbCoverLetter.exportDocx(md, { rtl: _cx.rtl() }).then(function () { _cx.track('export', { format: 'letter_docx' }); }, _cx.exportError('letter_docx'));
        });
        _pane.querySelector('[data-letter-copy]').addEventListener('click', function () {
            copy(globalThis.RbCoverLetter.plainText(md));
        });
    }

    /* ═══ hh.ru ═════════════════════════════════════════════════════════ */
    function hhBlocks() {
        var md = m(), p = md.personal, pr = md.prefs || {};
        var F = function (v) { return globalThis.RbI18n.formatDate(v, 'MM.YYYY'); };
        var out = [];
        if (p.title) out.push([T('hh_position'), p.title]);
        if (pr.salary) out.push([T('pref_salary'), pr.salary]);
        if (md.summary) out.push([T('hh_about'), md.summary]);
        (md.experience || []).forEach(function (e) {
            var body = [e.description, e.achievements].filter(function (x) { return String(x || '').trim(); }).join('\n');
            out.push([T('hh_job', { job: [e.position, e.company].filter(Boolean).join(' — '), dates: globalThis.RbI18n.dateRange(e.startDate, e.endDate, e.current, 'MM.YYYY', T('present')) }), body]);
        });
        var sk = (md.skills || []).map(function (k) { return k.name; }).filter(Boolean);
        if (sk.length) out.push([T('hh_skills'), sk.join('\n')]);
        (md.education || []).forEach(function (e) {
            out.push([T('hh_edu', { year: F(e.endDate) || '' }), [e.institution, [e.degree, e.field].filter(Boolean).join(', ')].filter(Boolean).join('\n')]);
        });
        return out;
    }
    function hhPane() {
        var h = head('nav_hh', 'pane_hh_sub');
        var blocks = hhBlocks();
        if (!blocks.length) return h + '<p class="rbx-hint">' + E(T('hh_empty')) + '</p>';
        h += blocks.map(function (b, i) {
            return '<div class="rbx-f"><div class="rbx-f-l"><span>' + E(b[0]) + '</span><button type="button" class="rbx-go" data-hh="' + i + '">📋 ' + E(T('copy_text')) + '</button></div>' +
                '<div class="rbx-in" style="white-space:pre-wrap;min-height:0;font-size:13px;line-height:1.5;max-height:180px;overflow:auto">' + E(b[1]) + '</div></div>';
        }).join('');
        return h;
    }
    function bindHh() {
        var blocks = hhBlocks();
        _pane.querySelectorAll('[data-hh]').forEach(function (b) {
            b.addEventListener('click', function () { copy(blocks[+b.dataset.hh][1]); _cx.track('hh_copy', { i: +b.dataset.hh }); });
        });
    }

    /* ═══ shared ════════════════════════════════════════════════════════ */
    function toast(msg, type) { if (window.__toast) window.__toast(msg, type || 'info'); }
    function copy(text) {
        var ok = function () { toast(T('copied'), 'success'); };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(ok, function () { fallbackCopy(text) && ok(); });
        } else if (fallbackCopy(text)) ok();
    }
    function fallbackCopy(text) {
        var ta = document.createElement('textarea');
        ta.value = text; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:absolute;left:-9999px;top:0';
        document.body.appendChild(ta); ta.select();
        var ok = false;
        try { ok = document.execCommand('copy'); } catch (e) {}
        ta.remove();
        return ok;
    }

    var _busy = null;
    function runInPane(btn, task, body, onDone) {
        /* the answer goes to the host right after the button's row (or inside it) —
           never into a neighbouring button */
        var host = btn.parentNode.querySelector('[data-ai-host]');
        if (!host) { var n = btn.parentNode.nextElementSibling; if (n && n.matches('[data-ai-host]')) host = n; }
        if (!host) { host = document.createElement('div'); host.setAttribute('data-ai-host', ''); btn.parentNode.insertAdjacentElement('afterend', host); }
        if (_busy) _busy.abort();
        var ctl = new AbortController();
        _busy = ctl;
        host.innerHTML = '';
        btn.disabled = true;
        var w = globalThis.RbAi.waitBlock(host, T('ai_working_' + task));
        w.stopBtn.addEventListener('click', function () { ctl.abort(); });
        globalThis.RbAi.run(task, body, { signal: ctl.signal, onWait: w.set }).then(function (res) {
            w.remove(); btn.disabled = false; _busy = null;
            onDone(res);
        }, function (e) {
            w.remove(); btn.disabled = false; _busy = null;
            if (e && e.kind === 'cancel') return;
            globalThis.RbAi.errorBlock(host, e || {}, function () { host.innerHTML = ''; btn.click(); });
        });
    }

    var PANES = { design: [designPane, bindDesign], check: [checkPane, bindCheck], job: [jobPane, bindJob], letter: [letterPane, bindLetter], hh: [hhPane, bindHh] };

    function show(paneId, keepScroll) {
        var p = PANES[paneId];
        if (!p || !_pane) return false;
        _cur = paneId;
        var top = _pane.scrollTop;
        _pane.innerHTML = p[0]();
        p[1]();
        _pane.scrollTop = keepScroll ? top : 0;
        return true;
    }
    function has(paneId) { return !!PANES[paneId]; }
    /* model changed elsewhere (another tab of the rail, an import): drop stale AI answers */
    function resetAnswers() { _review = null; _tailor = null; }

    function mount(paneEl, cx) { _pane = paneEl; _cx = cx; }

    globalThis.RbTools = { mount: mount, show: show, has: has, resetAnswers: resetAnswers, refTarget: refTarget, copy: copy };
})();
